/**
 * HorizontalLevelsPrimitive (R137) — draws Support/Resistance levels as
 * horizontal line segments on the price pane, each running from its formation
 * bar to the right edge of the chart, with an optional text label at the start.
 *
 * Used by the "S/R" indicator. lightweight-charts' createPriceLine() spans the
 * full width and stacks axis labels, which clutters with many levels; a
 * primitive gives per-level colour/width/style/opacity and a clean inline
 * label, and lets the line begin exactly where the level formed.
 *
 * Time units: UNIX seconds (IST-shifted, matching the chart's time axis).
 */
import type {
  ISeriesPrimitive,
  IPrimitivePaneView,
  IPrimitivePaneRenderer,
  SeriesAttachedParameter,
  Time,
  IChartApi,
  ISeriesApi,
  SeriesType,
} from 'lightweight-charts';
import type { CanvasRenderingTarget2D } from 'fancy-canvas';

export interface HLevel {
  price: number;
  /** Bar time where the level formed; the line starts here. */
  fromTime: number;
  /** CSS rgba/hex — opacity already baked in. */
  color: string;
  width: number;
  /** 'solid' | 'dashed' | 'dotted'. */
  style: 'solid' | 'dashed' | 'dotted';
  /** Optional inline text label drawn just above the line at its start. */
  label?: string;
}

function dash(style: HLevel['style'], w: number): number[] {
  switch (style) {
    case 'dotted': return [w, w * 2];
    case 'dashed': return [w * 4, w * 3];
    case 'solid':
    default: return [];
  }
}

class HLevelsRenderer implements IPrimitivePaneRenderer {
  constructor(
    private readonly levels: HLevel[],
    private readonly chart: IChartApi,
    private readonly series: ISeriesApi<SeriesType>,
  ) {}

  draw(target: CanvasRenderingTarget2D): void {
    if (!this.levels.length) return;
    const ts = this.chart.timeScale();
    const series = this.series;

    target.useBitmapCoordinateSpace(({ context: ctx, bitmapSize, horizontalPixelRatio: hpr, verticalPixelRatio: vpr }) => {
      ctx.save();
      const rightX = bitmapSize.width;
      for (const lv of this.levels) {
        const yC = series.priceToCoordinate(lv.price);
        if (yC == null) continue;
        const y = Number(yC) * vpr;
        // Left start: formation bar; clamp to left edge if it's off-screen left.
        const xc = ts.timeToCoordinate(lv.fromTime as Time);
        let x0 = xc == null ? 0 : Number(xc) * hpr;
        if (x0 < 0) x0 = 0;
        if (x0 > rightX) continue; // formed beyond the right edge — skip
        const w = Math.max(1, lv.width * Math.min(hpr, vpr));
        ctx.lineWidth = w;
        ctx.strokeStyle = lv.color;
        ctx.setLineDash(dash(lv.style, w));
        ctx.beginPath();
        ctx.moveTo(x0, y);
        ctx.lineTo(rightX, y);
        ctx.stroke();

        if (lv.label) {
          ctx.setLineDash([]);
          const fontPx = Math.round(10 * Math.min(hpr, vpr));
          ctx.font = `${fontPx}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;
          ctx.fillStyle = lv.color;
          ctx.textBaseline = 'bottom';
          // Label at the RIGHT end of the line (right-aligned just inside the
          // right edge), clamped so it never runs off the left of the segment.
          const lw2 = ctx.measureText(lv.label).width;
          const tx = Math.max(x0 + 2 * hpr, rightX - lw2 - 4 * hpr);
          ctx.fillText(lv.label, tx, y - 2 * vpr);
        }
      }
      ctx.restore();
    });
  }
}

class HLevelsPaneView implements IPrimitivePaneView {
  constructor(private readonly parent: HorizontalLevelsPrimitive) {}
  zOrder() { return 'top' as const; } // above candles so levels stay visible
  renderer(): IPrimitivePaneRenderer | null {
    const chart = this.parent.chart;
    const series = this.parent.series;
    if (!chart || !series) return null;
    return new HLevelsRenderer(this.parent.levels, chart, series);
  }
}

export class HorizontalLevelsPrimitive implements ISeriesPrimitive<Time> {
  levels: HLevel[] = [];
  chart: IChartApi | null = null;
  series: ISeriesApi<SeriesType> | null = null;
  private readonly _paneViews: HLevelsPaneView[];

  constructor(levels: HLevel[]) {
    this.levels = levels;
    this._paneViews = [new HLevelsPaneView(this)];
  }

  attached(params: SeriesAttachedParameter<Time>): void {
    this.chart = params.chart as IChartApi;
    this.series = params.series as ISeriesApi<SeriesType>;
  }
  detached(): void {
    this.chart = null;
    this.series = null;
  }

  updateAllViews(): void { /* renderer reads this.levels fresh each draw */ }
  paneViews(): readonly IPrimitivePaneView[] { return this._paneViews; }

  setData(levels: HLevel[]): void {
    this.levels = levels;
    this.chart?.applyOptions({});
  }
}
